// Defensive extraction of display fields from a gate doc's `target` map.
// The target is contractually drifting (serialized FairValueTarget,
// GATE.md: "display-only context, drifts constantly"), so nothing here
// trusts a field to exist or carry the right type — any junk degrades to
// null / skipped, never a throw (same principle as the RelativeDate fix).

export interface TargetContribution {
    name: string;
    score: number | null;
    /** normalized share of Σ positive weights; 0 when Σ ≤ 0 */
    share: number;
}

export interface TargetContext {
    outcome: string | null;
    mkt: number | null;
    fv: number | null;
    maxBuy: number | null;
    minSell: number | null;
    weight: number | null;
    /** fv − mkt, only when both present */
    edge: number | null;
    sizeLabel: string | null;
    timestamp: string | null;
    contributions: TargetContribution[];
}

function num(v: unknown): number | null {
    if (typeof v !== 'number' && typeof v !== 'string') return null;
    const n = typeof v === 'string' && v.trim() === '' ? NaN : Number(v);
    return Number.isFinite(n) ? n : null;
}

/** Probabilities/prices live on [0,1]; junk outside renders nowhere. */
function prob(v: unknown): number | null {
    const n = num(v);
    return n !== null && n >= 0 && n <= 1 ? n : null;
}

function str(v: unknown): string | null {
    return typeof v === 'string' && v ? v : null;
}

function rec(v: unknown): Record<string, unknown> {
    return v && typeof v === 'object' && !Array.isArray(v)
        ? (v as Record<string, unknown>)
        : {};
}

function parseContributions(metaData: unknown): TargetContribution[] {
    const raw = rec(metaData).conviction_contributions;
    if (!Array.isArray(raw)) return [];
    const entries = raw.flatMap(e => {
        const { name, score, weight } = rec(e);
        const n = str(name);
        const w = num(weight);
        // name and a non-negative weight are required; score is optional.
        if (n === null || w === null || w < 0) return [];
        return [{ name: n, score: num(score), weight: w }];
    });
    const total = entries.reduce((s, e) => s + e.weight, 0);
    return entries.map(({ name, score, weight }) => ({
        name,
        score,
        share: total > 0 ? weight / total : 0,
    }));
}

/** Human-readable market question, defensively pulled from the drifting target. */
export function parseMarketQuestion(
    target: Record<string, unknown> | undefined | null
): string | null {
    if (!target || typeof target !== 'object') return null;
    return str(rec(target.market_info).question);
}

export function parseTargetContext(
    target: Record<string, unknown> | undefined | null
): TargetContext | null {
    if (!target || typeof target !== 'object') return null;
    const info = rec(target.market_info);

    const prices = Array.isArray(info.outcome_prices)
        ? info.outcome_prices
        : [];
    const idx = num(info.outcome_index);
    const mkt =
        idx !== null && idx >= 0 && idx < prices.length
            ? prob(prices[idx])
            : null;
    const fv = prob(target.probability);
    if (mkt === null && fv === null) return null;

    const size = rec(target.position_size);
    const sizeValue = num(size.value);
    const sizeType = str(size.type);
    const sizeLabel =
        sizeValue !== null
            ? `$${sizeValue}${sizeType ? ` ${sizeType}` : ''}`
            : null;

    return {
        outcome: str(info.outcome),
        mkt,
        fv,
        maxBuy: prob(target.max_buy_price),
        minSell: prob(target.min_sell_price),
        weight: num(target.weight),
        edge: mkt !== null && fv !== null ? fv - mkt : null,
        sizeLabel,
        timestamp: str(target.timestamp),
        contributions: parseContributions(target.meta_data),
    };
}
